import { readableDay } from '../dateLabels';
import { periodTone, type PeriodTone } from '../habitVocab';
import { HABIT_PERIOD_NOUN } from '../taskVocab';
import { periodFigure } from './HabitHistory';

/**
 * The habit's recent run, one cell per period.
 *
 * It used to be the *current period* split into days, which meant a daily habit got a single cell
 * and drew nothing at all — twenty of thirty-one habits on the health nut's account, and exactly
 * the ones logged every morning. The row carrying the longest live streak was the row with the
 * least on it. The old guard's reasoning ("for a daily habit the strip is one cell, which is
 * honest") was answering the wrong question: the window should be the last N *periods*, not the
 * inside of one.
 *
 * Counting periods fixes two more things at once.
 *
 * The strip and the streak beside it now measure the same unit. Before, a weekly habit drew days
 * inside this week while the text next to it counted weeks, so the two never described the same
 * quantity.
 *
 * And `met` is direction-aware, so a **clean** period on an avoid habit fills its cell. The strip
 * used to invert against every other use of colour in the app: "Alcohol — 54 clean days · 7 weeks
 * in a row" drew seven *empty* cells, identical to a habit never logged once, while a single slip
 * on Added sugar was the darkest green on the screen.
 *
 * Structural props rather than a `DayHabit`, because the Today rail and the Habits screen hold the
 * same numbers under different names.
 *
 * **One colour language with the chart.** The cells wear the 12-week chart's `tone-*` classes from
 * the same `periodTone`, and a cell below target fills *part* of its height in the chart's orange,
 * as far as it got. Before, the strip had its own vocabulary — `is-partial` was one light green for
 * any `0 < total < target`, so 16.4 of 18 and 10.54 of 18 drew alike, and directly below it the
 * chart drew those same weeks orange (`01a08096-e70c`). Re-colouring `is-partial` orange would have
 * fixed the second half and kept the first; grading by ratio is the chart's own answer, at a cell's
 * height.
 */
export function PeriodStrip({
  strip,
  name,
  period,
  polarity = 'positive',
  today,
  periodOver = false,
  target,
  unit,
  reading = false,
}: {
  strip: {
    key: string;
    met: boolean;
    total: number;
    isCurrent: boolean;
    applicable: boolean;
    /**
     * Nothing was recorded, and for this habit that is an absence rather than a zero.
     *
     * Not the same question as `applicable`, which is false for **three** reasons: this one, a
     * period before the habit existed, and one after it was paused. All three have no figure and
     * each has a different sentence — nobody took a reading, there was no habit yet, the habit is
     * stood down — which is why the title below asks all three before stating a figure.
     */
    unmeasured: boolean;
    /** Stood down before this period — see `paused` on `HabitStripCell`. */
    paused: boolean;
  }[];
  name: string;
  period: string;
  /**
   * Which direction the habit runs, because a part-filled cell only means progress in one of them.
   *
   * On an avoid habit `met` is *clean*, so a cell with something in it is a slip, not a partial
   * success. A slip past the allowance is a whole missed period, and fills its cell orange.
   */
  polarity?: 'positive' | 'negative';
  /** The day on screen, which is not always the current one — Today can be paged. */
  today: string;
  /**
   * The period the day on screen falls in has ended on the account's calendar — `periodIsOver`.
   *
   * The server marks that period `isCurrent` because it judges a paged-back day as that day (Phase
   * 87), so the cell drew *still running* over yesterday (`01a15a31-5ea4`). It is a finished period
   * and is drawn and titled as one; it keeps its `is-today` dot, which marks the day on screen.
   */
  periodOver?: boolean;
  /** The period's target, which a cell's fill and its title are both stated against. */
  target: number;
  unit: string | null;
  /**
   * A reading rather than a tally — the service's `isReading`. 78.84 kg against 78 is not 99% of
   * a workload, so a reading's cell is not graded: it is met or it is not.
   */
  reading?: boolean;
}) {
  if (strip.length === 0) return null;

  const noun = HABIT_PERIOD_NOUN[period] ?? HABIT_PERIOD_NOUN.week!;
  /*
    The span, said. Twelve weekly cells and fourteen daily ones stacked in one list looked like the
    same chart — the planner's Guitar practice and Read Book rows — and the label read "over the
    last weeks" with no number in it. `strip.length` is the number, and the chart's own heading
    ("Last 12 weeks") is the wording.
  */
  const span = `last ${strip.length} ${strip.length === 1 ? noun.one : noun.many}`;

  return (
    <div className="habit-strip-row">
      <div className="habit-strip" role="img" aria-label={`${name} over the ${span}`}>
        {strip.map((served) => {
          const cell = periodOver && served.isCurrent ? { ...served, isCurrent: false } : served;
          const tone = periodTone(cell);
          const fill = fillFraction(cell, tone, target, polarity, reading);
          return (
            <span
              key={cell.key}
              data-period={cell.key}
              className={[
                'habit-strip-cell',
                `tone-${tone}`,
                /*
                  Today is the cell you look for after logging. A dot rather than a fill, so it
                  stays visible whatever the tone.

                  A daily habit's current period *is* today; for longer periods the cell containing
                  the day on screen is the one to mark, which `isCurrent` already knows.
                */
                served.isCurrent || cell.key === today ? 'is-today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              title={cellTitle(cell, tone, period, target, unit, polarity, reading)}
            >
              {fill > 0 && (
                <span
                  className="habit-strip-fill"
                  style={{ height: `${Math.round(fill * 100)}%` }}
                />
              )}
            </span>
          );
        })}
      </div>
      {/* Hidden from the accessibility tree because the label above already says it. */}
      <span className="habit-strip-span" aria-hidden="true">
        {span}
      </span>
    </div>
  );
}

type Cell = {
  key: string;
  met: boolean;
  total: number;
  isCurrent: boolean;
  applicable: boolean;
  unmeasured: boolean;
  paused: boolean;
};

/**
 * How much of the cell is filled, 0–1.
 *
 * Nothing for a period with no figure; all of it for a period at target, on either polarity — a
 * clean week on an avoid habit fills its cell, which is the inversion this strip once had the other
 * way round. Otherwise the share of the target reached, which is what separates 16.4 of 18 from
 * 10.54 of 18 and a slip from a blow-out; a ceiling past its limit reaches 1.
 */
function fillFraction(
  cell: Cell,
  tone: PeriodTone,
  target: number,
  polarity: 'positive' | 'negative',
  reading: boolean,
): number {
  if (tone === 'inapplicable' || tone === 'unmeasured' || cell.unmeasured) return 0;
  if (cell.met && (tone === 'met' || polarity === 'positive')) return 1;
  if (reading) return 1;
  if (target <= 0) return cell.total > 0 ? 1 : 0;
  return Math.max(0, Math.min(1, cell.total / target));
}

/**
 * Which period a cell is, as a person reads it — *19 Oct* for a day, *week of 13 Oct* for a week.
 *
 * It printed the period's ISO key (`2026-10-19: 0 minutes of 90 — missed`), the one spelling
 * `dateLabels.ts` says nothing a person reads uses. The key stays on the cell as `data-period`,
 * which is where anything after the machine form reads it.
 *
 * A week or a month is named the way the chart under the strip names its bars — `week of 13 Oct`,
 * `month of 1 Oct`, from the same `HABIT_PERIOD_NOUN` and the same day — so hovering one period on
 * either surface names it in the same words. A day is just the day, as the year grid's squares
 * write it: *day of 19 Oct* says nothing *19 Oct* does not. `readableRange` (*13–19 Oct*) was the
 * other candidate for a week and was not taken, because it would have named one week two ways
 * a few pixels apart.
 */
function periodName(key: string, period: string): string {
  const day = readableDay(key);
  if (period === 'day') return day;
  const noun = HABIT_PERIOD_NOUN[period] ?? HABIT_PERIOD_NOUN.week!;
  return `${noun.one} of ${day}`;
}

/**
 * The hover text: the period, the figure against the target, then the verdict.
 *
 * It printed `${cell.total}` raw, so a week of 16.8 + 1.1 read `2026-07-13: 17.900000000000002` —
 * on the one surface used to check a total by hand (`01a08094-6da4`) — and it printed no target,
 * so the number could not be checked against anything. The figure is `periodFigure()`, which the
 * chart's bar tooltip states too, so hovering the same week on either says one thing.
 *
 * A missed period says so. It used to say nothing after the figure, which left "judged and short"
 * to be inferred from the absence of "— met".
 */
function cellTitle(
  cell: Cell,
  tone: PeriodTone,
  period: string,
  target: number,
  unit: string | null,
  polarity: 'positive' | 'negative',
  reading: boolean,
): string {
  const name = periodName(cell.key, period);
  /*
    The three absences first, each with its own sentence — paused, then not measured, then not yet
    a habit — because a nought on any of them is invented. The before-it-started cell printed
    `2026-06-01: 0` until this; the chart has said "before this habit started" all along.
  */
  if (cell.paused) return `${name}: paused`;
  if (cell.unmeasured) return `${name}: not measured`;
  if (!cell.applicable) return `${name}: before this habit started`;
  const figure = periodFigure(cell.total, target, unit, polarity, reading);
  /*
    A period that has not finished has not been met — `isCurrent` decides it before `met` is read,
    for every polarity. `2026-08-24: 0 — met` on a Wednesday, for an avoid habit with four days of
    allowance still to spend, was a verdict on a week nobody had lived yet.
  */
  const verdict = tone === 'current' ? 'still running' : cell.met ? 'met' : 'missed';
  return `${name}: ${figure} — ${verdict}`;
}

/**
 * What the strip's colours mean, said once per screen.
 *
 * Four states were distinguishable only in computed styles — "never started" transparent, "missed"
 * light grey — and neither the strip nor the chart had a key. One line, drawn with the strip's own
 * classes, so a key and a cell cannot drift apart.
 */
export function StripLegend() {
  const items: { tone: PeriodTone; fill: number; label: string }[] = [
    { tone: 'met', fill: 1, label: 'met' },
    { tone: 'missed', fill: 0.5, label: 'short of target' },
    { tone: 'missed', fill: 0, label: 'nothing done' },
    { tone: 'unmeasured', fill: 0, label: 'not measured' },
    { tone: 'inapplicable', fill: 0, label: 'not started or paused' },
    { tone: 'current', fill: 0.5, label: 'still running' },
  ];
  return (
    <p className="strip-legend" data-testid="strip-legend">
      {items.map((item) => (
        <span key={item.label} className="strip-legend-item">
          <span className={`strip-swatch tone-${item.tone}`} aria-hidden="true">
            {item.fill > 0 && (
              <span className="habit-strip-fill" style={{ height: `${item.fill * 100}%` }} />
            )}
          </span>
          {item.label}
        </span>
      ))}
    </p>
  );
}
